'use client';

import { useEffect, useState } from 'react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import UserContextProvider from '@/context/Auth';
import { PostHogProvider } from '@/context/PostHogProvider';
import { Toaster } from '@/components/ui/sonner';
import { initAmplify } from '@/lib/amplify-config';
import {
  markDeploymentHealthy,
  reloadIfStaleDeployment,
} from '@/lib/deploymentSkew';

initAmplify();

export function Providers({ children }: { children: React.ReactNode }) {
  // Every Server Action call from a tab left open across a deploy fails with
  // "Server Action ... was not found". Most of them run through react-query,
  // so recover here rather than in each page: reload once to pick up the new
  // build, and clear the guard again as soon as a request succeeds.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: reloadIfStaleDeployment,
          onSuccess: markDeploymentHealthy,
        }),
        mutationCache: new MutationCache({
          onError: reloadIfStaleDeployment,
          onSuccess: markDeploymentHealthy,
        }),
      })
  );

  // The few Server Action calls made outside react-query surface as
  // unhandled rejections instead.
  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => {
      if (reloadIfStaleDeployment(event.reason)) event.preventDefault();
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <UserContextProvider>
        <PostHogProvider>
          <ReactQueryDevtools />
          {children}
          <Toaster />
        </PostHogProvider>
      </UserContextProvider>
    </QueryClientProvider>
  );
}
